import { memo, useMemo } from 'react';
import { Line } from '@react-three/drei';
import { View } from '../core/types';
import { useBIMStore } from '../state/useBIMStore';
import { extractCrossLines } from './BIMOverlayLayer';

/**
 * BIM Drawing Underlay (hybrid 2D drawings)
 *
 * When the 2D workbench edits a drawing view that is derived from the BIM
 * model (FloorPlan / CeilingPlan / AreaPlan / Section / Elevation), this
 * layer projects the model's cross-reference wireframes into the drawing's
 * 2D plane as a read-only underlay:
 *
 * - Plan views: top-down projection — the model's XY footprint flattens
 *   onto the drafting plane (Z-up world, so dropping Z gives the plan).
 * - Section / Elevation views: orthographic projection onto the view's
 *   cut plane (horizontal extent × height), drawn in the drafting plane.
 *
 * Standalone 2D drawings (Drawing / Detail / Drafting / Legend) never get
 * an underlay — their canvas is genuinely empty. The underlay is purely
 * visual: no click handlers, no selection registry, always beneath the
 * editable entity layer.
 */
const UNDERLAY_COLOR = '#4a5a6a';
const PLAN_TYPES = new Set(['FloorPlan', 'CeilingPlan', 'AreaPlan']);
const SECTION_TYPES = new Set(['Section', 'Elevation']);

type Pt2 = [number, number];

export const BimDrawingUnderlay = memo(function BimDrawingUnderlay({ viewId }: { viewId: string }) {
    const view = useBIMStore((s) => s.project.views.get(viewId));
    const elements = useBIMStore((s) => s.project.elements);

    const polylines = useMemo(() => {
        if (!view) return [];
        if (!PLAN_TYPES.has(view.type) && !SECTION_TYPES.has(view.type)) return [];
        const project = PLAN_TYPES.has(view.type) ? projectPlan : projectCutPlane(view);
        const lines: Pt2[][] = [];
        for (const element of elements.values()) {
            for (const pts of extractCrossLines(element)) {
                const flat = pts.map(project);
                // Skip degenerate segments (e.g. vertical edges in plan)
                const hasLength = flat.some((p, i) =>
                    i > 0 && (Math.abs(p[0] - flat[i - 1][0]) > 1e-6 || Math.abs(p[1] - flat[i - 1][1]) > 1e-6));
                if (hasLength) lines.push(flat);
            }
        }
        return lines;
    }, [view, view?.type, view?.cutPlane, elements]);

    if (polylines.length === 0) return null;

    return (
        <group>
            {polylines.map((points, i) => (
                <Line
                    key={i}
                    points={points.map(([x, y]) => [x, y, 0] as [number, number, number])}
                    color={UNDERLAY_COLOR}
                    lineWidth={1}
                    transparent
                    opacity={0.8}
                />
            ))}
        </group>
    );
});

/** Plan projection: the Z-up footprint flattens onto the XY drafting plane. */
function projectPlan(p: [number, number, number]): Pt2 {
    return [p[0], p[1]];
}

/**
 * Section / elevation projection: orthographic view onto the cut plane.
 * Horizontal axis runs along the plane's in-ground direction, the vertical
 * axis keeps true height (Z-up). Falls back to a +Y-facing section when the
 * view carries no explicit cut plane.
 */
function projectCutPlane(view: View): (p: [number, number, number]) => Pt2 {
    const origin = view.cutPlane?.origin ?? [0, 0, 0];
    const normal = view.cutPlane?.normal ?? [0, 1, 0];
    const len = Math.hypot(normal[0], normal[1]) || 1;
    // In-ground direction of the cut plane (unit, horizontal)
    const u: [number, number] = [-normal[1] / len, normal[0] / len];
    return (p) => [
        (p[0] - origin[0]) * u[0] + (p[1] - origin[1]) * u[1],
        p[2] - origin[2],
    ];
}
